import { ISSUE_HELP, SEVERITY_EXPLANATION } from '../issueHelp';
import type { Issue, Severity } from '../types';

const GROUPS: { severity: Severity; heading: string; icon: string; cls: string; label: string; none: string }[] = [
  { severity: 'BlockingError', heading: 'Blocking errors', icon: '✕', cls: 'phi-issue--error', label: 'Blocking error', none: 'No blocking errors.' },
  { severity: 'Warning', heading: 'Warnings requiring review', icon: '!', cls: 'phi-issue--warning', label: 'Warning', none: 'No warnings.' },
  { severity: 'Information', heading: 'Informational messages', icon: 'i', cls: 'phi-issue--info', label: 'Information', none: 'No informational messages.' },
];

interface Props {
  issues: Issue[];
  truncated: boolean;
  /** Show only these severities (default: all three). */
  only?: Severity[];
  /** Add a plain-language "what it means / what to do" under each finding that has one. */
  explain?: boolean;
}

/** Findings grouped as errors (must fix), warnings (please review) and information. Severity is stated in words as
 * well as by icon and colour. The server's message and code are always shown unchanged. */
export function IssueGroups({ issues, truncated, only, explain = false }: Props) {
  const groups = only ? GROUPS.filter((g) => only.includes(g.severity)) : GROUPS;
  return (
    <>
      {groups.map((g) => {
        const items = issues.filter((i) => i.severity === g.severity);
        const headingId = `phi-issues-${g.severity}`;
        return (
          <section key={g.severity} aria-labelledby={headingId}>
            <h3 id={headingId}>
              {g.heading} ({items.length})
            </h3>
            {items.length === 0 ? (
              <p className="muted">{g.none}</p>
            ) : (
              <>
                {explain && <p className="muted">{SEVERITY_EXPLANATION[g.severity]}</p>}
                <ul className="phi-issues" aria-labelledby={headingId}>
                  {items.map((issue, index) => {
                    const where = [issue.file, issue.rowNumber !== null ? `row ${issue.rowNumber}` : null, issue.sourceRecordId ? `record ${issue.sourceRecordId}` : null]
                      .filter(Boolean)
                      .join(' · ');
                    const help = explain ? ISSUE_HELP[issue.code] : undefined;
                    return (
                      <li key={`${issue.code}-${index}`} className={`phi-issue ${g.cls}`}>
                        <strong>
                          <span aria-hidden="true">{g.icon} </span>
                          {g.label}
                        </strong>
                        <span>
                          <code>{issue.code}</code> {issue.message}
                        </span>
                        {where && <span className="phi-issue-where">{where}</span>}
                        {help && (
                          <span className="phi-plain">
                            <strong>What it means:</strong> {help.meaning} <strong>What to do:</strong> {help.action}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>
        );
      })}
      {truncated && <p className="muted">Only the first 500 issues are shown. Download the validation report for the full list.</p>}
    </>
  );
}
